import { useMemo, useState } from 'react';
import { Plus } from 'lucide-react';
import { useAuth } from 'utils/authcontextt';
import { useAgentRosterQuery } from 'hooks/useAgents';
import type { AgentRosterEntry } from 'types/agents';
import { AgentCard } from 'components/agents/AgentCard';
import { AgentEditPanel } from 'components/agents/AgentEditPanel';
import { AddAgentModal } from 'components/agents/AddAgentModal';
import { AgentDetailView } from 'components/agents/AgentDetailView';
import { AgentStatusToggle } from 'components/agents/AgentStatusToggle';

export function AgentsPage() {
  const { user } = useAuth();
  const isAdmin = user?.role === 'admin';
  const { data: roster = [], isLoading } = useAgentRosterQuery();

  const [addOpen, setAddOpen] = useState(false);
  const [editEntry, setEditEntry] = useState<AgentRosterEntry | null>(null);
  const [detailUserId, setDetailUserId] = useState<string | null>(null);

  const front = useMemo(() => roster.filter((a) => a.office === 'front'), [roster]);
  const back = useMemo(() => roster.filter((a) => a.office === 'back'), [roster]);

  const renderGroup = (label: string, items: AgentRosterEntry[]) => (
    <section className="mb-6">
      <div className="mb-2 flex items-center gap-2">
        <span className="text-sm font-medium text-gray-900">{label}</span>
        <span className="text-xs text-gray-400">{items.length} agenter</span>
      </div>
      {items.length === 0 ? (
        <div className="rounded-xl border border-dashed border-gray-200 p-4 text-sm text-gray-400">
          Inga agenter här.
        </div>
      ) : (
        <div className="grid grid-cols-[repeat(auto-fill,minmax(180px,1fr))] gap-3">
          {items.map((entry) => (
            <AgentCard
              key={entry.userId}
              entry={entry}
              canEdit={isAdmin}
              onSelect={() => setDetailUserId(entry.userId)}
              onEdit={() => setEditEntry(entry)}
            />
          ))}
        </div>
      )}
    </section>
  );

  return (
    <div className="h-[calc(100vh-96px)] overflow-y-auto bg-gray-50 p-6">
      <header className="mb-6 flex items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl font-semibold text-gray-950">Agenter</h1>
          <p className="mt-1 text-sm text-gray-500">
            Vem som svarar i chatten – och vilket namn kunden ser.
          </p>
        </div>
        <div className="flex items-center gap-3">
          <AgentStatusToggle />
          {isAdmin && (
            <button
              type="button"
              onClick={() => setAddOpen(true)}
              className="inline-flex items-center gap-1.5 rounded-lg bg-gray-950 px-3 py-2 text-sm font-medium text-white transition hover:bg-gray-800"
            >
              <Plus className="h-4 w-4" />
              Lägg till agent
            </button>
          )}
        </div>
      </header>

      {isLoading ? (
        <div className="text-sm text-gray-500">Laddar agenter…</div>
      ) : roster.length === 0 ? (
        <div className="rounded-xl border border-dashed border-gray-200 p-6 text-center text-sm text-gray-400">
          Inga agenter ännu.{isAdmin ? ' Lägg till en för att börja.' : ''}
        </div>
      ) : (
        <>
          {renderGroup('Front office', front)}
          {renderGroup('Back office', back)}
        </>
      )}

      {addOpen && <AddAgentModal onClose={() => setAddOpen(false)} />}
      {editEntry && <AgentEditPanel entry={editEntry} onClose={() => setEditEntry(null)} />}
      {detailUserId && (
        <AgentDetailView userId={detailUserId} onClose={() => setDetailUserId(null)} />
      )}
    </div>
  );
}

export default AgentsPage;
